import { useEffect, useMemo, useState } from "react";
import { SectionLabel } from "@/components/SectionLabel";
import { WordReveal } from "@/components/WordReveal";
import { projects, projectTags, type Project } from "@/content/projects";

const ALL = "Alle";

export function Projects() {
  const [activeTag, setActiveTag] = useState(ALL);
  const [selected, setSelected] = useState<Project | null>(null);
  const visible = useMemo(
    () => (activeTag === ALL ? projects : projects.filter((p) => p.tags.includes(activeTag))),
    [activeTag],
  );

  return (
    <section
      id="projects"
      className="border-t border-line px-6 py-20 md:px-10 lg:px-16"
      aria-labelledby="projects-h"
    >
      <header className="mx-auto mb-14 flex max-w-7xl flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <SectionLabel index={5}>Prosjekter</SectionLabel>
          <WordReveal
            as="h2"
            id="projects-h"
            className="mt-5 block font-display text-5xl font-normal leading-[1.02] md:text-6xl"
          >
            Utvalgte prosjekter
          </WordReveal>
        </div>
        <div className="flex flex-wrap gap-2" aria-label="Filter prosjekter">
          {[ALL, ...projectTags].map((tag) => (
            <button
              key={tag}
              type="button"
              aria-pressed={activeTag === tag}
              onClick={() => setActiveTag(tag)}
              className="border border-line bg-panel px-3 py-1.5 font-mono text-[0.65rem] uppercase tracking-widest text-muted transition hover:border-accent hover:text-accent focus-visible:border-accent focus-visible:text-accent focus-visible:outline-none aria-pressed:border-accent aria-pressed:bg-accent aria-pressed:text-bg"
            >
              {tag}
            </button>
          ))}
        </div>
      </header>

      <ul className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((project, i) => (
          <ProjectCard key={project.title} project={project} index={i} onSelect={setSelected} />
        ))}
      </ul>

      {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function ProjectCard({
  project,
  onSelect,
}: {
  project: Project;
  index: number;
  onSelect: (project: Project) => void;
}) {
  return (
    <li className="card-lift overflow-hidden border border-line bg-panel">
      <button
        type="button"
        onClick={() => onSelect(project)}
        className="group block h-full w-full text-left focus-visible:outline-none"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={project.cover.src}
            alt={project.cover.alt}
            loading="lazy"
            className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]"
          />
          <span className="bg-bg/60 absolute right-4 top-4 border border-line px-2 py-1 font-mono text-[0.62rem] uppercase tracking-widest text-fg backdrop-blur">
            {project.year}
          </span>
        </div>
        <div className="space-y-4 p-6">
          <h3 className="font-display text-2xl font-normal text-fg group-hover:text-accent">
            {project.title}
          </h3>
          <p className="text-sm leading-relaxed text-muted">{project.summary}</p>
          <TagList tags={project.tags} />
        </div>
      </button>
    </li>
  );
}

function ProjectModal({ project, onClose }: { project: Project; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="bg-bg/85 fixed inset-0 z-50 grid place-items-center p-5 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-h"
    >
      <div className="max-h-full w-full max-w-3xl overflow-auto border border-line bg-bg">
        <img src={project.cover.src} alt="" className="aspect-video w-full object-cover" />
        <div className="space-y-6 p-7">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted">
                {project.year}
              </p>
              <h3 id="project-modal-h" className="mt-2 font-display text-3xl font-normal">
                {project.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="border border-line px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-muted hover:border-accent hover:text-accent focus-visible:border-accent focus-visible:text-accent focus-visible:outline-none"
            >
              Lukk
            </button>
          </div>
          <p className="leading-relaxed text-muted">{project.description}</p>
          <TagList tags={project.tags} />
          {project.links.length > 0 && (
            <div className="flex flex-wrap gap-4 pt-2">
              {project.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-mono text-xs uppercase tracking-widest text-accent hover:underline focus-visible:underline focus-visible:outline-none"
                >
                  {link.label} →
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TagList({ tags }: { tags: ReadonlyArray<string> }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <li
          key={tag}
          className="border border-line bg-panel px-2 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted"
        >
          {tag}
        </li>
      ))}
    </ul>
  );
}
